// Handles transient Next.js chunk / CSS / module-loading failures — the classic
// "Loading chunk 7203 failed" that fires when a user's browser holds a stale
// bundle after a deploy, or on a flaky / blocked connection. These are NOT code
// bugs: they self-heal on a page refresh. So instead of leaving the user on a
// broken error screen and emailing the owner about it, we recover silently by
// reloading the page once.
//
// Safety: a one-shot sessionStorage flag prevents a reload loop if the chunk is
// genuinely missing (a broken deploy). GlobalErrorHandler clears that flag a few
// seconds after a healthy mount, so future transient failures in the same
// session can recover again.
//
// Pure client code (window/sessionStorage/location only) — safe to import from
// any client component, including the special app/global-error.tsx boundary.

// React #306 ("Element type is invalid... lazy must resolve to a class or
// function") is included on purpose: in production it is almost always a stale
// tab holding a pre-deploy chunk graph (observed 2026-09-13: hits on a 2-day-
// old deployment's chunks). A genuine lazy-import bug would survive the single
// guarded reload and get reported on the second pass, so this stays safe.
const CHUNK_LOAD_RE =
  /Loading (?:chunk|CSS chunk) \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed|Minified React error #306|invariant=306/i;

const RELOAD_FLAG = "changelyric:chunk-reload-attempted";

export function isChunkLoadError(message?: string, stack?: string): boolean {
  const text = `${message || ""} ${stack || ""}`;
  return CHUNK_LOAD_RE.test(text);
}

// Returns true if a reload was triggered (caller should stop and NOT report).
// Returns false if we already reloaded once this session and it still failed —
// in that case the caller should report normally, since this looks like a
// genuinely broken deploy rather than a transient miss.
export function recoverChunkLoadFailure(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(RELOAD_FLAG)) {
      window.sessionStorage.removeItem(RELOAD_FLAG);
      return false;
    }
    window.sessionStorage.setItem(RELOAD_FLAG, "1");
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

// Called after the app mounts healthily: schedule clearing the one-shot flag so
// a later transient failure in the same session can recover again.
export function clearChunkReloadFlagAfter(delayMs = 5000): void {
  if (typeof window === "undefined") return;
  try {
    if (!window.sessionStorage.getItem(RELOAD_FLAG)) return;
    window.setTimeout(() => {
      try {
        window.sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        // ignore
      }
    }, delayMs);
  } catch {
    // ignore
  }
}
